import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { 
  FiMenu, FiShoppingCart, FiSend, 
  FiMonitor, FiTablet, FiSmartphone, FiX 
} from 'react-icons/fi';
import GlobalFooter, { DEFAULT_FOOTER } from './GlobalFooter';
import ProductsWidget from './widgets/ProductsWidget';
import ReviewsWidget from './widgets/ReviewsWidget';
import ArtistWidget from './widgets/ArtistWidget';
import { useCart } from '../../context/CartContext';

const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const smoothScrollTo = (container, targetY, duration = 900) => {
  const startY = container.scrollTop;
  const distance = targetY - startY;
  let startTime = null;

  const step = (timestamp) => {
    if (!startTime) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    container.scrollTop = startY + distance * easeInOutCubic(progress);
    
    if (progress < 1) requestAnimationFrame(step);
  };
  
  requestAnimationFrame(step);
};

// Inline contact form for preview (read-only / functional)
const PreviewContactForm = ({ styles = {} }) => {
  const {
    buttonText = 'Send Message',
    buttonBgColor = '#2dd4bf',
    buttonTextColor = '#ffffff',
    borderRadius = 12,
    formBgColor = '#ffffff',
    inputBgColor = '#f9fafb',
    textColor = '#111827',
    destinationEmail = '',
  } = styles;

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (destinationEmail) {
      const form = e.target;
      const firstName = form.firstName?.value || '';
      const lastName = form.lastName?.value || '';
      const email = form.email?.value || '';
      const message = form.message?.value || '';
      
      const subject = encodeURIComponent('New Contact Form Submission');
      const body = encodeURIComponent(`Name: ${firstName} ${lastName}\nEmail: ${email}\n\nMessage:\n${message}`);
      window.location.href = `mailto:${destinationEmail}?subject=${subject}&body=${body}`;
    }
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  const inputStyle = {
    backgroundColor: inputBgColor,
    borderRadius: `${Math.max(0, borderRadius - 4)}px`,
    color: textColor,
  };

  if (submitted) {
    return (
      <div 
        className="w-full max-w-xl mx-auto p-8 shadow-sm border border-gray-100 flex flex-col items-center justify-center space-y-4"
        style={{ backgroundColor: formBgColor, borderRadius: `${borderRadius}px`, minHeight: 200 }}
      >
        <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center">
          <FiSend className="w-6 h-6 text-teal-500" />
        </div>
        <p className="font-black text-lg" style={{ color: textColor }}>Message Sent!</p>
        <p className="text-sm text-gray-400">Thank you for reaching out. We'll get back to you soon.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto p-8 shadow-sm border border-gray-100" style={{ backgroundColor: formBgColor, borderRadius: `${borderRadius}px` }}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-5">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: textColor }}>First Name</label>
            <input name="firstName" type="text" required className="w-full p-3.5 outline-none focus:ring-2 focus:ring-teal-400/30 transition-all text-sm font-medium border border-gray-200" style={inputStyle} placeholder="John" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: textColor }}>Last Name</label>
            <input name="lastName" type="text" required className="w-full p-3.5 outline-none focus:ring-2 focus:ring-teal-400/30 transition-all text-sm font-medium border border-gray-200" style={inputStyle} placeholder="Doe" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: textColor }}>Email Address</label>
          <input name="email" type="email" required className="w-full p-3.5 outline-none focus:ring-2 focus:ring-teal-400/30 transition-all text-sm font-medium border border-gray-200" style={inputStyle} placeholder="john@example.com" />
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: textColor }}>Message</label>
          <textarea name="message" required rows="4" className="w-full p-3.5 outline-none focus:ring-2 focus:ring-teal-400/30 transition-all text-sm font-medium resize-none border border-gray-200" style={inputStyle} placeholder="How can we help you?" />
        </div>
        <button 
          type="submit" 
          className="w-full p-4 font-black transition-transform hover:scale-[1.02] flex items-center justify-center space-x-2 shadow-lg"
          style={{ backgroundColor: buttonBgColor, color: buttonTextColor, borderRadius: `${Math.max(0, borderRadius - 2)}px` }}
        >
          <span>{buttonText}</span>
          <FiSend className="w-4 h-4 ml-1" />
        </button>
      </form>
    </div>
  );
};

const LivePreviewOverlay = ({
  elements = [],
  headerPadding,
  canvasPadding,
  logoData,
  menuItems = [],
  menuStyles = {},
  footerData,
  onClose,
  isLiveMode = false,
  siteSlug,
  onNavigate,
}) => {
  const [deviceView, setDeviceView] = useState('desktop');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const { cartCount, setIsCartOpen } = useCart();

  // Scroll to top anytime elements change (simulates page navigation)
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [elements]);

  const headerBgColor = menuStyles.backgroundColor || '#ffffff';
  const menuTextColor = menuStyles.textColor || '#4b5563';
  const menuHoverColor = menuStyles.hoverColor || '#2dd4bf';

  const getContainerWidthClass = () => {
    switch (deviceView) {
      case 'tablet': return 'max-w-[768px]';
      case 'mobile': return 'max-w-[375px]';
      default: return 'w-full';
    }
  };

  const handleNavClick = (e, link, linkType) => {
    if (!link) {
      e.preventDefault();
      return;
    }
    
    const isAnchor = linkType === 'anchor' || link.startsWith('#');
    
    if (isAnchor) {
      e.preventDefault();
      const anchorId = link.startsWith('#') ? link.substring(1) : link;
      const target = document.getElementById(`preview-section-${anchorId}`);
      const container = scrollContainerRef.current;
      
      if (target && container) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        // Calculate relative position to scroll container
        const targetY = container.scrollTop + (targetRect.top - containerRect.top) - 80; // 80px offset for sticky header
        smoothScrollTo(container, targetY, 900);
        setIsMobileMenuOpen(false);
      }
    } else if (linkType === 'page' || (!linkType && link.startsWith('/'))) {
      e.preventDefault();
      if (onNavigate) {
        // Use SPA navigation frame replacement
        onNavigate(link);
      } else {
        // Fallback for isolated preview mode
        const pagePath = link === '/' ? '' : (link.startsWith('/') ? link : `/${link}`);
        const targetPath = siteSlug ? `/${siteSlug}${pagePath}` : pagePath;
        window.location.href = targetPath;
      }
      setIsMobileMenuOpen(false);
    } else {
      // URL type (external link)
      if (!isLiveMode) {
        e.preventDefault();
        window.open(link, '_blank');
      }
    }
  };

  const renderWidget = (el) => {
    if (el.type === 'container') {
      const { 
        backgroundType, 
        backgroundColor, 
        backgroundImage, 
        overlayOpacity = 0, 
        overlayColor = '#000000',
        anchorId 
      } = el.styles || {};
      
      const pTop = el.styles?.paddingTop !== undefined ? el.styles.paddingTop : 48;
      const pBottom = el.styles?.paddingBottom !== undefined ? el.styles.paddingBottom : 48;

      const bgStyle = backgroundType === 'image' && backgroundImage
        ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { backgroundColor: backgroundColor || '#ffffff' };

      return (
        <div id={anchorId ? `preview-section-${anchorId}` : undefined} key={el.id} style={{ ...bgStyle, paddingTop: pTop, paddingBottom: pBottom }} className="w-full relative">
          {backgroundType === 'image' && backgroundImage && overlayOpacity > 0 && (
            <div className="absolute inset-0 pointer-events-none z-[1]" style={{ backgroundColor: overlayColor, opacity: overlayOpacity }} />
          )}
          <div className="flex flex-col md:flex-row w-full gap-4 px-6 relative z-[2]">
            {el.columns?.map((col, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-stretch space-y-1">
                {col.map(widget => renderWidget(widget))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    const mTop = el.styles?.marginTop || 0;
    const mBottom = el.styles?.marginBottom || 0;
    let content = null;

    if (el.type === 'heading') {
      content = (
        <h2 
          style={{ 
            color: el.styles?.color, 
            fontWeight: el.styles?.fontWeight, 
            fontStyle: el.styles?.fontStyle, 
            textDecoration: el.styles?.textDecoration,
            fontSize: `${el.styles?.fontSize || 48}px`,
            textAlign: el.styles?.textAlign || 'center',
            lineHeight: '1.2',
          }} 
          className="font-black tracking-tight w-full whitespace-pre-wrap"
        >
          {el.content}
        </h2>
      );
    } else if (el.type === 'text') {
      content = (
        <p 
          style={{ 
            color: el.styles?.color, 
            fontWeight: el.styles?.fontWeight, 
            fontStyle: el.styles?.fontStyle, 
            textDecoration: el.styles?.textDecoration,
            fontSize: `${el.styles?.fontSize || 16}px`,
            textAlign: el.styles?.textAlign || 'left',
            lineHeight: '1.6',
          }} 
          className="w-full whitespace-pre-wrap"
        >
          {el.content}
        </p>
      );
    } else if (el.type === 'image') {
      content = el.styles?.imageUrl ? <img src={el.styles.imageUrl} alt={el.styles.altText || 'Image'} className="w-full h-auto object-cover rounded-lg" /> : null;
    } else if (el.type === 'button') {
      const s = el.styles || {};
      content = (
        <div className="flex justify-center w-full">
          <a 
            href={s.link || '#'} 
            onClick={(e) => handleNavClick(e, s.link, s.linkType)}
            className="px-10 py-4 font-black text-lg transition-all hover:scale-105 inline-block text-center cursor-pointer"
            style={{ backgroundColor: s.bgColor, color: s.textColor, borderRadius: `${s.borderRadius || 12}px` }}
          >
            {s.text || 'Get Started'}
          </a>
        </div>
      );
    } else if (el.type === 'spacer') {
      content = <div style={{ height: `${el.styles?.height || 50}px` }} className="w-full" />;
    } else if (el.type === 'products') {
      content = <ProductsWidget styles={el.styles} />;
    } else if (el.type === 'contact-form') {
      content = <PreviewContactForm styles={el.styles} />;
    } else if (el.type === 'reviews') {
      content = <ReviewsWidget styles={el.styles} />;
    } else if (el.type === 'artist') {
      content = <ArtistWidget styles={el.styles} />;
    }

    if (!content) return null;

    return (
      <div key={el.id} className="w-full py-2 px-4" style={{ marginTop: `${mTop}px`, marginBottom: `${mBottom}px` }}>
        {content}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[99999] flex flex-col bg-[#0a0a0a]">
      
      {/* Top Banner Toolbar */}
      <div className="h-14 bg-[#0a0a0a] text-white flex items-center justify-between px-4 border-b border-[#1e2228] shrink-0 z-50">
        <div className="flex items-center space-x-3">
          <div className="w-7 h-7 bg-[#c8f542] rounded-md flex items-center justify-center">
            <span className="text-black font-black text-xs">S</span>
          </div>
          <span className="font-bold text-sm tracking-wider text-white hidden sm:block">LIVE PREVIEW</span>
        </div>

        <div className="flex items-center bg-[#1e2228] rounded-lg p-1 gap-1 border border-[#2c313a]">
          {[['desktop', FiMonitor], ['tablet', FiTablet], ['mobile', FiSmartphone]].map(([mode, Icon]) => (
            <button
              key={mode}
              onClick={() => setDeviceView(mode)}
              className={`p-2 rounded-lg transition-colors ${
                deviceView === mode ? 'text-[#c8f542] bg-[#c8f542]/10' : 'hover:text-gray-200 text-gray-500'
              }`}
              title={`Switch to ${mode} view`}
            >
              <SafeIcon icon={Icon} className="w-4 h-4" />
            </button>
          ))}
        </div>

        <button 
          onClick={onClose} 
          className="flex items-center space-x-2 px-4 py-2 bg-[#1e2228] hover:bg-[#2c313a] border border-[#2c313a] text-gray-300 hover:text-white rounded-full text-xs font-black uppercase tracking-widest transition-all"
        >
          <SafeIcon icon={FiX} className="w-3.5 h-3.5" />
          <span className="hidden sm:block">Exit Preview</span>
        </button>
      </div>

      {/* Scrollable Container with Device Frame constraints */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto bg-[#f0f2f5] flex justify-center custom-scrollbar">
        <div className={`bg-white shadow-2xl transition-all duration-300 flex flex-col ${getContainerWidthClass()}`} style={{ minHeight: '100%', width: '100%' }}>
          
          {/* Sticky Header */}
          <div className="sticky top-0 z-30">
            <header 
              className="px-8 flex justify-between items-center border-b border-gray-100 transition-all"
              style={{ 
                backgroundColor: headerBgColor, 
                paddingTop: `${headerPadding?.top ?? 32}px`, 
                paddingBottom: `${headerPadding?.bottom ?? 32}px`,
              }}
            >
              <div 
                className="cursor-pointer transition-opacity hover:opacity-80" 
                onClick={(e) => handleNavClick(e, '/', 'page')}
              >
                {logoData?.type === 'text' ? (
                  <div className="text-2xl font-black text-gray-900 tracking-tighter">{logoData.content}</div>
                ) : logoData?.url ? (
                  <img src={logoData.url} className="h-10 w-auto" alt="logo" />
                ) : (
                  <div className="text-2xl font-black text-gray-900 tracking-tighter">BRAND LOGO</div>
                )}
              </div>

              <div className="flex items-center space-x-6">
                <nav className="hidden md:flex items-center space-x-8 border-r border-gray-200 pr-6 mr-2">
                  {menuItems.map(item => (
                    <a 
                      key={item.id} 
                      href={item.link || '#'} 
                      onClick={(e) => handleNavClick(e, item.link, item.linkType)}
                      className="text-sm font-bold transition-colors"
                      style={{ color: menuTextColor }}
                      onMouseEnter={(e) => e.target.style.color = menuHoverColor}
                      onMouseLeave={(e) => e.target.style.color = menuTextColor}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>

                <div className="flex items-center space-x-4">
                  <span 
                    className="hidden md:block text-sm font-bold cursor-pointer"
                    style={{ color: menuTextColor }}
                    onMouseEnter={(e) => e.target.style.color = menuHoverColor}
                    onMouseLeave={(e) => e.target.style.color = menuTextColor}
                  >
                    Login
                  </span>

                  <div 
                    className="relative cursor-pointer"
                    style={{ color: menuTextColor }}
                    onClick={() => setIsCartOpen(true)}
                  >
                    <FiShoppingCart className="w-5 h-5" />
                    <span className="absolute -top-1.5 -right-1.5 bg-teal-500 text-white text-[9px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  </div>

                  <div className="relative md:hidden">
                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 rounded-lg hover:bg-black/5 transition-colors">
                      <FiMenu className="w-6 h-6" style={{ color: menuTextColor }} />
                    </button>

                    <AnimatePresence>
                      {isMobileMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full right-0 mt-2 bg-white shadow-2xl rounded-xl border border-gray-100 min-w-[200px] overflow-hidden z-[100]"
                        >
                          <div className="py-2">
                            {menuItems.map(item => (
                              <a 
                                key={item.id} 
                                href={item.link || '#'} 
                                onClick={(e) => handleNavClick(e, item.link, item.linkType)}
                                className="block px-5 py-3 text-sm font-semibold hover:bg-gray-50 transition-colors"
                                style={{ color: menuTextColor }}
                              >
                                {item.label}
                              </a>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </header>
          </div>

          {/* Page Body */}
          <div style={{ paddingTop: `${canvasPadding?.top ?? 64}px`, paddingBottom: `${canvasPadding?.bottom ?? 64}px` }} className="flex-1">
            {elements.length === 0 ? (
              <div className="flex items-center justify-center py-32 text-gray-300">
                <p className="font-black text-sm tracking-widest uppercase">No content to preview</p>
              </div>
            ) : (
              <div className="w-full">{elements.map(el => renderWidget(el))}</div>
            )}
          </div>

          {/* Global Footer */}
          <GlobalFooter 
            footerData={footerData || DEFAULT_FOOTER} 
            isEditable={false} 
            siteSlug={siteSlug}
            onNavClick={handleNavClick}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default LivePreviewOverlay;